import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Flag, Clock, CheckCircle2, XCircle, ChevronLeft, ChevronRight, Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function ExamSimulatorPage() {
  const { certId, attemptId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [current, setCurrent] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [mode, setMode] = useState("exam");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);

  const submit = useCallback(async () => {
    clearInterval(timerRef.current);
    setSubmitting(true);
    try {
      const { data } = await api.post(`/certifications/${certId}/exam/${attemptId}/submit`);
      setResult(data);
      setMode("results");
    } catch (e) {
      toast.error("Couldn't submit the exam. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [certId, attemptId]);

  useEffect(() => {
    api.get(`/certifications/${certId}/exam/${attemptId}`).then(({ data }) => {
      if (data.status === "completed") {
        setResult(data);
        setMode("results");
        return;
      }
      setQuestions(data.questions);
      setAnswers(data.answers || {});
      setFlagged(new Set(data.flagged || []));
      setTimeLeft(data.time_remaining_seconds);
    });
  }, [certId, attemptId]);

  const hasActiveTimer = timeLeft !== null;
  const timeLeftRef = useRef(timeLeft);
  useEffect(() => { timeLeftRef.current = timeLeft; }, [timeLeft]);

  useEffect(() => {
    if (!hasActiveTimer || mode !== "exam") return;
    if (timeLeftRef.current <= 0) { submit(); return; }
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          submit();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [hasActiveTimer, mode, submit]);

  const selectAnswer = (qid, optionIndex) => {
    setAnswers((prev) => ({ ...prev, [qid]: optionIndex }));
    api.put(`/certifications/${certId}/exam/${attemptId}/answer`, { question_id: qid, selected_index: optionIndex }).catch(() => {});
  };

  const toggleFlag = (qid) => {
    const isFlagged = flagged.has(qid);
    const next = new Set(flagged);
    if (isFlagged) next.delete(qid); else next.add(qid);
    setFlagged(next);
    api.put(`/certifications/${certId}/exam/${attemptId}/flag`, { question_id: qid, flagged: !isFlagged }).catch(() => {});
  };

  if (!questions) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="exam-simulator-loading"><div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (mode === "results" && result) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="exam-results-page">
        <div className="glass-panel rounded-2xl p-8 text-center mb-6" data-testid="exam-final-score">
          <Trophy className={`w-10 h-10 mx-auto mb-3 ${result.passed ? "text-amber-400" : "text-slate-500"}`} />
          <p className="font-heading text-4xl font-bold text-white">{result.score}%</p>
          <p className={`text-sm font-semibold mt-1 ${result.passed ? "text-emerald-400" : "text-rose-400"}`}>{result.passed ? "Passed" : "Not Passed"} · {result.correct_count}/{result.total} correct</p>
        </div>

        {result.weak_areas?.length > 0 && (
          <div className="glass-panel rounded-2xl p-6 mb-6" data-testid="exam-weak-areas">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">Weak Areas</h3>
            <div className="space-y-2">
              {result.weak_areas.map((w) => (
                <div key={w.topic} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">{w.topic}</span>
                  <span className="text-rose-400 font-semibold">{w.score_pct}% ({w.correct}/{w.total})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3" data-testid="exam-question-breakdown">
          {result.results.map((r, i) => (
            <div key={r.id} className="glass-panel rounded-xl p-4" data-testid={`exam-result-question-${i}`}>
              <div className="flex items-start gap-2">
                {r.correct ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />}
                <p className="text-sm text-white">{i + 1}. {r.question}</p>
              </div>
              <p className="text-xs text-slate-400 mt-2 ml-6">Correct answer: {r.options[r.correct_index]}</p>
              <p className="text-xs text-slate-500 mt-1 ml-6">{r.explanation}</p>
            </div>
          ))}
        </div>

        <button onClick={() => navigate(`/certifications/${certId}`)} data-testid="exam-back-to-cert-btn" className="mt-6 w-full bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
          Back to {certId.replace(/-/g, " ")}
        </button>
      </div>
    );
  }

  if (mode === "review") {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="exam-review-page">
        <h2 className="font-heading text-2xl font-semibold text-white mb-1">Review Your Answers</h2>
        <p className="text-sm text-slate-400 mb-6">{Object.keys(answers).length}/{questions.length} answered · {flagged.size} flagged</p>
        <div className="grid grid-cols-5 gap-2 mb-8" data-testid="exam-review-grid">
          {questions.map((q, i) => (
            <button
              key={q.id} onClick={() => { setCurrent(i); setMode("exam"); }}
              data-testid={`exam-review-jump-${i}`}
              className={`aspect-square rounded-lg text-sm font-semibold flex items-center justify-center relative transition-colors ${
                answers[q.id] !== undefined ? "bg-emerald-500/15 text-emerald-300 border border-emerald-400/30" : "bg-white/5 text-slate-400 border border-white/10"
              }`}
            >
              {i + 1}
              {flagged.has(q.id) && <Flag className="w-3 h-3 text-amber-400 absolute -top-1 -right-1" />}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={() => setMode("exam")} data-testid="exam-review-back-btn" className="flex-1 border border-white/10 hover:border-white/20 text-white font-semibold px-6 py-3 rounded-lg transition-colors">Back to Exam</button>
          <button onClick={submit} disabled={submitting} data-testid="exam-final-submit-btn" className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Exam"}
          </button>
        </div>
      </div>
    );
  }

  const q = questions[current];
  const isFlagged = flagged.has(q.id);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="exam-simulator-page">
      <div className="flex items-center justify-between mb-6">
        <span className="text-sm text-slate-400">Question {current + 1} of {questions.length}</span>
        <div className={`flex items-center gap-1.5 text-sm font-bold tabular-nums ${timeLeft < 60 ? "text-rose-400" : "text-indigo-300"}`} data-testid="exam-timer">
          <Clock className="w-4 h-4" /> {formatTime(timeLeft)}
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-6" data-testid="exam-question-card">
        <div className="flex items-start justify-between gap-3 mb-4">
          <p className="text-base font-medium text-white">{q.question}</p>
          <button onClick={() => toggleFlag(q.id)} data-testid="exam-flag-btn" className={`shrink-0 p-2 rounded-lg transition-colors ${isFlagged ? "bg-amber-400/15 text-amber-400" : "text-slate-500 hover:text-slate-300"}`}>
            <Flag className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-2.5">
          {q.options.map((opt, oi) => (
            <button
              key={oi} onClick={() => selectAnswer(q.id, oi)} data-testid={`exam-option-${oi}`}
              className={`w-full text-left text-sm px-4 py-3 rounded-lg border transition-colors ${
                answers[q.id] === oi ? "border-indigo-400/50 bg-indigo-400/10 text-indigo-300" : "border-white/10 text-slate-300 hover:border-white/20"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <button onClick={() => setCurrent((c) => Math.max(0, c - 1))} disabled={current === 0} data-testid="exam-prev-btn" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white disabled:opacity-30">
          <ChevronLeft className="w-4 h-4" /> Previous
        </button>
        <button onClick={() => setMode("review")} data-testid="exam-go-to-review-btn" className="text-sm font-semibold text-indigo-400 hover:text-indigo-300">Review & Submit</button>
        {current < questions.length - 1 ? (
          <button onClick={() => setCurrent((c) => Math.min(questions.length - 1, c + 1))} data-testid="exam-next-btn" className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : <span className="w-16" />}
      </div>
    </div>
  );
}
